import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppContext } from "@/context/AppContext";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";

type TablePayload = {
  name?: string;
};

export function TableListRoute() {
  const navigate = useNavigate();
  const { blocks, isLoading } = useAppContext();
  const { setBreadcrumbSegments } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbSegments([{ label: "Tables" }]);
    return () => setBreadcrumbSegments([]);
  }, [setBreadcrumbSegments]);

  const tables = useMemo(() => {
    const rows: { id: string; name: string }[] = [];
    for (const block of blocks) {
      if (block.type !== "table") continue;
      const data = block.data as TablePayload;
      rows.push({
        id: block.id,
        name: typeof data.name === "string" ? data.name : "",
      });
    }
    return rows;
  }, [blocks]);

  return (
    <div className="flex min-w-0 flex-1 flex-col p-8">
      <Card className="w-full">
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : tables.length === 0 ? (
            <p className="text-muted-foreground text-sm">No tables.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead className="w-14 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tables.map(({ id, name }) => (
                  <TableRow
                    key={id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/tables/${id}`)}
                  >
                    <TableCell className="font-medium">{name || "—"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-xs" aria-label="Row actions">
                            <span className="text-base leading-none">...</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/tables/${id}/edit`}>Edit</Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
